import { skipToken } from '@apollo/client/react'
import { uniqBy } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { match, P } from 'ts-pattern'
import { useInfiniteListArtworks } from 'src/services/apollo/artwork'
import { useMyInfo } from 'src/services/apollo/auth'
import type {
  ArtworkPreviewFragment,
  ArtworkType,
  ListArtworksQueryVariables,
} from 'src/services/apollo/generated/graphql'
import {
  isOrderOptionPosSeq,
  OrderOption,
} from 'src/services/helpers/orderOptionParams'
import { useOrpcDailyRankingArtworks } from 'src/services/orpc/dailyRankingArtwork'
import { useOrpcFollowingNewsArtworks } from 'src/services/orpc/followingNewsArtwork'
import { useOrpcNewestArtworks } from 'src/services/orpc/newestArtwork'
import { useOrpcRecommendArtworks } from 'src/services/orpc/recommendArtwork'
import { useOrpcSimilarArtworksInfinite } from 'src/services/orpc/similarArtwork'
import { useOrpcTrendingArtworks } from 'src/services/orpc/trendingArtwork'
import dayjs from 'src/utils/dayjs'
import { getNodesFromConnection } from 'src/utils/graphql'
import { normalizeSearch } from 'src/utils/string'

/**
 * TRANSITION STATE — slated for cleanup in Q3 2026.
 *
 * Today the frontend has one entry point (`listArtworks` / GraphQL `artworks`)
 * that the backend then routes internally to many backends (Redis trending,
 * Elasticsearch, recommendation service, daily-ranking, …) based on a tangle
 * of `feed`/`order`/`tag`/`q`/etc. flags. `getFeedCategory` below replicates
 * a small slice of that routing on the client so we can peel a few feeds off
 * onto dedicated REST endpoints (`/v2/artwork/recommend`,
 * `/v2/artwork/feed/trending`) without changing how the existing feeds work.
 *
 * The end state is one URL per feed kind (e.g. `/v2/artwork/feed/trending`,
 * `/v2/artwork/feed/latest`, `/v2/artwork/feed/following`, search and tag/tack
 * pages on their own routes, etc.). Each of those endpoints will accept only
 * the inputs that feed actually honors, instead of the current "throw every
 * arg at one query and hope the server picks the right backend" pattern.
 *
 * Until that work lands in Q3 2026, the client-side branching here — and the
 * `hasFilters` guard inside `getFeedCategory` — exists purely to avoid
 * sending requests to the new endpoints that the backend trending resolver
 * would have silently dropped to ES anyway. Do not extend this with new feed
 * branches; add a new dedicated endpoint and route through that instead.
 */
export const FeedCategory = {
  /** Recommendation feeds served by the dedicated recommendArtworks query */
  Recommendation: 'recommendation',
  /** Trending feed served by the dedicated /v2/artwork/feed/trending endpoint */
  Trending: 'trending',
  /** Daily-ranking feed served by the dedicated /v2/artwork/daily-ranking endpoint */
  DailyRanking: 'daily-ranking',
  /** Following-news feed served by /v2/artwork/feed/following-news */
  FollowingNews: 'following-news',
  /** Newest feed served by the dedicated /v2/artwork/feed/newest endpoint */
  Newest: 'newest',
  /** All other feeds served by the artworks query */
  Default: 'default',
} as const

export type FeedCategoryType = (typeof FeedCategory)[keyof typeof FeedCategory]

export function getListArtworkFeedFromArgs(args: {
  order?: string
  type?: ArtworkType
  relevantArtworkId?: string
  q?: string
  tag?: string
}) {
  return (
    match(args)
      .with(
        { order: P.union(OrderOption.random, OrderOption.trending) },
        () => 'trending1',
      )
      // recommend-v1 is now handled by the dedicated recommendArtworks query
      .with({ order: OrderOption.followingNews }, () => 'following_news')
      .with({ order: OrderOption.newest }, () => 'latest')
      .with({ relevantArtworkId: P.string.minLength(1) }, () => 'relevant-v2')
      .otherwise(() => undefined)
  )
}

// ---------------------------------------------------------------------------
// Shared result shape
// ---------------------------------------------------------------------------

export interface ArtworkFeedResult {
  artworks: ArtworkPreviewFragment[]
  loading: boolean
  loadingMore: boolean
  hasNext: boolean
  feedUuid?: string
  loadMore: () => void
}

// ---------------------------------------------------------------------------
// useListArtworkFeed — GraphQL artworks query (tag, q, authorId, etc.)
// ---------------------------------------------------------------------------

export interface UseListArtworkFeedArgs {
  tag?: string
  tackId?: string
  q?: string
  relevantArtworkId?: string
  order?: string
  type?: ArtworkType
  i2vPro?: boolean
  challenge?: number
  authorId?: string
  pageSize?: number
  extraArgs?: Record<string, any>
}

/**
 * **GraphQL** — `artworks` query via Apollo.
 *
 * Use when the feed requires filters: tag, tack, search query, author,
 * challenge, relevant artwork, type, or i2vPro. Supports all `order` values.
 *
 * Pass `null` or `skipToken` to suspend fetching (e.g. while a dependency is
 * still loading). Auth-state gating is handled internally via `useMyInfo`.
 */
export function useListArtworkFeed(
  input: UseListArtworkFeedArgs | null | typeof skipToken,
): ArtworkFeedResult {
  const { loading: userLoading } = useMyInfo()

  const inputArgs = input !== null && input !== skipToken ? input : null
  const hasInputArgs = !!inputArgs

  const tag = inputArgs?.tag
  const tackId = inputArgs?.tackId
  const q = inputArgs?.q
  const relevantArtworkId = inputArgs?.relevantArtworkId
  const order = inputArgs?.order
  const type = inputArgs?.type
  const i2vPro = inputArgs?.i2vPro
  const challenge = inputArgs?.challenge
  const authorId = inputArgs?.authorId
  const pageSize = inputArgs?.pageSize ?? 24
  const extraArgs = inputArgs?.extraArgs

  const isPosSeq = isOrderOptionPosSeq(order)

  const parsedArgs: Parameters<typeof useInfiniteListArtworks>[0] =
    useMemo(() => {
      if (userLoading || !hasInputArgs) return skipToken

      const args: ListArtworksQueryVariables = {
        ...extraArgs,
        isSafeSearch: true,
        tag,
        tackId,
        q: typeof q === 'string' ? normalizeSearch(q) : undefined,
        relevantArtworkId,
        authorId,
        type,
        i2vPro,
      }

      if (isPosSeq) {
        args.first = pageSize
      } else {
        args.last = pageSize
      }

      args.feed = getListArtworkFeedFromArgs({
        order,
        type,
        relevantArtworkId,
        q,
        tag,
      })

      if (order === OrderOption.dailyRanking) {
        if (!i2vPro && (q == null || q === '')) {
          args.feed = 'daily_ranking_dedup'
        } else {
          /**
           * Cap daily ranking by likedCount across the last week, but
           * exclude the most-recent 30min so freshly-created artworks
           * don't churn the top of the list.
           */
          args.orderBy = '-markInfo.likedCount'
          args.time = {
            gt: dayjs().subtract(1, 'week').startOf('day').toISOString(),
            lt: dayjs().subtract(30, 'minute').startOf('minute').toISOString(),
          }
        }
      }

      if (order === OrderOption.mostLiked) {
        args.orderBy = '-markInfo.likedCount'
      }

      if (tag === 'challenge') {
        args.challenge = challenge
        args.tag = undefined
      }

      if (tag === 'all' || !tag) {
        args.tag = undefined
      }

      return args
    }, [
      hasInputArgs,
      userLoading,
      extraArgs,
      tag,
      tackId,
      q,
      relevantArtworkId,
      authorId,
      type,
      i2vPro,
      isPosSeq,
      pageSize,
      order,
      challenge,
    ])

  const listResult = useInfiniteListArtworks(parsedArgs, {
    fetchPolicy: 'cache-first',
  })

  const listData = listResult.data

  const artworks = useMemo(() => {
    if (!listData) return []
    const nodes = uniqBy(getNodesFromConnection(listData), 'id')
    return isPosSeq ? nodes : nodes.reverse()
  }, [listData, isPosSeq])

  const hasNext = isPosSeq
    ? (listData?.pageInfo?.hasNextPage ?? false)
    : (listData?.pageInfo?.hasPreviousPage ?? false)

  // Apollo's listArtworks needs a manual guard against duplicate fetchMore
  // calls because notifyOnNetworkStatusChange is off.
  const fetchingRef = useRef(false)
  useEffect(() => {
    fetchingRef.current = false
  }, [artworks])

  const loadMore = useCallback(() => {
    if (userLoading || listResult.loading || listResult.loadingMore || !hasNext)
      return
    if (fetchingRef.current) return
    fetchingRef.current = true
    listResult
      .fetchMore({
        variables: isPosSeq
          ? ({
              after: listData?.pageInfo.endCursor,
              first: pageSize,
            } as Partial<ListArtworksQueryVariables>)
          : ({
              before: listData?.pageInfo.startCursor,
              last: pageSize,
            } as Partial<ListArtworksQueryVariables>),
      })
      .catch(() => {
        fetchingRef.current = false
      })
  }, [userLoading, listResult, hasNext, listData, isPosSeq, pageSize])

  return {
    artworks,
    loading: userLoading || listResult.loading,
    loadingMore: listResult.loadingMore,
    hasNext: !!hasNext,
    feedUuid: listData?.feedUuid,
    loadMore,
  }
}

// ---------------------------------------------------------------------------
// useRecommendedArtworkFeed — /v2/artwork/recommend
// ---------------------------------------------------------------------------

/**
 * **oRPC** — `/v2/artwork/recommend` (TanStack Query).
 *
 * Use for the personalised "Recommended" feed with no additional filters.
 * Routes to an authenticated or visitor endpoint based on login state.
 * Returns a `feedUuid` for recommendation tracking.
 *
 * Pass `null` to disable (e.g. when a different feed is active).
 */
export function useRecommendedArtworkFeed(
  input: { first?: number } | null,
): ArtworkFeedResult {
  const { loading: userLoading } = useMyInfo()

  const result = useOrpcRecommendArtworks(
    userLoading || !input ? null : { first: input.first ?? 24 },
  )

  const loadMore = useCallback(() => {
    // Bail while a page is in flight — `BaseArtworkInfiniteScroll`'s scroll
    // listener fires loadMore on every event, and TanStack's fetchNextPage
    // cancels the prior in-flight request by default. Without this guard
    // the network panel fills with cancelled requests until the trigger
    // element scrolls out of view. Also bail on `userLoading`: the query
    // is disabled while my-info is refetching, but TanStack's
    // `fetchNextPage` ignores `enabled` and would still fire against the
    // cached `hasNextPage`.
    if (
      userLoading ||
      result.loading ||
      result.loadingMore ||
      !result.pageInfo.hasNextPage
    )
      return
    result.fetchMore()
  }, [userLoading, result])

  return {
    artworks: result.artworks,
    loading: userLoading || result.loading,
    loadingMore: result.loadingMore,
    hasNext: result.pageInfo.hasNextPage,
    feedUuid: result.feedUuid,
    loadMore,
  }
}

// ---------------------------------------------------------------------------
// useTrendingArtworkFeed — /v2/artwork/feed/trending
// ---------------------------------------------------------------------------

/**
 * **oRPC** — `/v2/artwork/feed/trending` (TanStack Query).
 *
 * Use for the unfiltered "Trending / Random" feed.
 * Pass `null` to skip the query. Does not return a `feedUuid`.
 */
export function useTrendingArtworkFeed(
  input: { first?: number } | null,
): ArtworkFeedResult {
  const { loading: userLoading } = useMyInfo()

  const result = useOrpcTrendingArtworks(
    userLoading || !input ? null : { first: input.first ?? 24 },
  )

  const loadMore = useCallback(() => {
    // See `useRecommendedArtworkFeed` for the rationale — bail while a
    // fetchNextPage is in flight, or while my-info is refetching, so
    // scroll-driven loadMore calls don't pile up cancelled requests.
    if (
      userLoading ||
      result.loading ||
      result.loadingMore ||
      !result.pageInfo.hasNextPage
    )
      return
    result.fetchMore()
  }, [userLoading, result])

  return {
    artworks: result.artworks,
    loading: userLoading || result.loading,
    loadingMore: result.loadingMore,
    hasNext: result.pageInfo.hasNextPage,
    feedUuid: undefined,
    loadMore,
  }
}

// ---------------------------------------------------------------------------
// useNewestArtworkFeed — /v2/artwork/feed/newest
// ---------------------------------------------------------------------------

/**
 * **oRPC** — `/v2/artwork/feed/newest` (TanStack Query).
 *
 * Use for the unfiltered "Newest" feed (public artworks sorted by createdAt
 * desc). Pass `null` to skip the query. Does not return a `feedUuid`.
 */
export function useNewestArtworkFeed(
  input: { first?: number } | null,
): ArtworkFeedResult {
  const { loading: userLoading } = useMyInfo()

  const result = useOrpcNewestArtworks(
    userLoading || !input ? null : { first: input.first ?? 24 },
  )

  const loadMore = useCallback(() => {
    // See `useRecommendedArtworkFeed` for the rationale — bail while a
    // fetchNextPage is in flight, or while my-info is refetching, so
    // scroll-driven loadMore calls don't pile up cancelled requests.
    // (TanStack's `fetchNextPage` ignores `enabled` and would still fire
    // against the cached `hasNextPage` while userLoading is true.)
    if (
      userLoading ||
      result.loading ||
      result.loadingMore ||
      !result.pageInfo.hasNextPage
    )
      return
    result.fetchMore()
  }, [userLoading, result])

  return {
    artworks: result.artworks,
    loading: userLoading || result.loading,
    loadingMore: result.loadingMore,
    hasNext: result.pageInfo.hasNextPage,
    feedUuid: undefined,
    loadMore,
  }
}

// ---------------------------------------------------------------------------
// useDailyRankingArtworkFeed — /v2/artwork/daily-ranking
// ---------------------------------------------------------------------------

/**
 * **oRPC** — `/v2/artwork/daily-ranking` (TanStack Query).
 *
 * Use for the unfiltered "Daily Ranking" feed (one artwork per author,
 * ranked by likedCount). Pass `null` to skip the query. Does not return
 * a `feedUuid`.
 */
export function useDailyRankingArtworkFeed(
  input: { first?: number } | null,
): ArtworkFeedResult {
  const { loading: userLoading } = useMyInfo()

  const result = useOrpcDailyRankingArtworks(
    userLoading || !input ? null : { first: input.first ?? 24 },
  )

  const loadMore = useCallback(() => {
    // Bail while a page is in flight — the scroll handler in
    // `BaseArtworkInfiniteScroll` re-fires loadMore on every scroll event,
    // and TanStack's fetchNextPage cancels the prior in-flight call by
    // default. Without this guard the network panel fills with cancelled
    // requests until the trigger element finally scrolls out of view.
    if (result.loading || result.loadingMore || !result.pageInfo.hasNextPage)
      return
    result.fetchMore()
  }, [result])

  return {
    artworks: result.artworks,
    loading: userLoading || result.loading,
    loadingMore: result.loadingMore,
    hasNext: result.pageInfo.hasNextPage,
    feedUuid: undefined,
    loadMore,
  }
}

// ---------------------------------------------------------------------------
// useFollowingNewsArtworkFeed — /v2/artwork/feed/following-news
// ---------------------------------------------------------------------------

/**
 * **oRPC** — `/v2/artwork/feed/following-news` (TanStack Query).
 *
 * Use for the unfiltered "Following / News" feed (latest public artwork from
 * authors the current user follows). Anonymous callers get an empty page;
 * the empty-state copy is owned by `BaseArtworkInfiniteScroll`.
 *
 * Pass `null` to skip the query. Does not return a `feedUuid`.
 */
export function useFollowingNewsArtworkFeed(
  input: { first?: number } | null,
): ArtworkFeedResult {
  const { loading: userLoading } = useMyInfo()

  const result = useOrpcFollowingNewsArtworks(
    userLoading || !input ? null : { first: input.first ?? 24 },
  )

  const loadMore = useCallback(() => {
    // Bail while a page is in flight — same rationale as
    // `useDailyRankingArtworkFeed` above: the scroll handler in
    // `BaseArtworkInfiniteScroll` re-fires loadMore on every scroll event
    // and TanStack would otherwise cancel the in-flight request each time.
    // Also bail while `useMyInfo()` is refetching, since the underlying
    // query is disabled in that window but `fetchNextPage` would still
    // fire against the cached `hasNextPage`.
    if (
      userLoading ||
      result.loading ||
      result.loadingMore ||
      !result.pageInfo.hasNextPage
    )
      return
    result.fetchMore()
  }, [userLoading, result])

  return {
    artworks: result.artworks,
    loading: userLoading || result.loading,
    loadingMore: result.loadingMore,
    hasNext: result.pageInfo.hasNextPage,
    feedUuid: undefined,
    loadMore,
  }
}

// ---------------------------------------------------------------------------
// useSimilarArtworkFeed — /v2/artwork/{artworkId}/similar
// ---------------------------------------------------------------------------

/**
 * **oRPC** — `/v2/artwork/{artworkId}/similar` (TanStack Query).
 *
 * Use for the "similar / relevant artworks" feed scoped to a single
 * artwork. Pass `null` to skip the query. Does not return a `feedUuid`.
 */
export function useSimilarArtworkFeed(
  input: { artworkId: string; first?: number } | null,
): ArtworkFeedResult {
  const { loading: userLoading } = useMyInfo()

  const result = useOrpcSimilarArtworksInfinite(
    userLoading || !input
      ? null
      : { artworkId: input.artworkId, first: input.first ?? 24 },
  )

  const loadMore = useCallback(() => {
    // See `useRecommendedArtworkFeed` for the rationale.
    if (
      userLoading ||
      result.loading ||
      result.loadingMore ||
      !result.pageInfo.hasNextPage
    )
      return
    result.fetchMore()
  }, [userLoading, result])

  return {
    artworks: result.artworks,
    loading: userLoading || result.loading,
    loadingMore: result.loadingMore,
    hasNext: result.pageInfo.hasNextPage,
    feedUuid: undefined,
    loadMore,
  }
}
